import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Activity,
  Workflow,
  ShieldCheck,
  PlugZap,
  LineChart,
  Wand2,
  Layers,
  Lock,
  Mail,
  RotateCw,
  ArrowLeft,
  ArrowRight as ArrowRightIcon,
  Menu,
  Search,
  Split,
} from "lucide-react";

import FaqSection from "../components/FaqSection";
import AskUsSection from "../components/AskUsSection";

const heroBg =
  "https://static.prod-images.emergentagent.com/jobs/74c18d57-ed75-40f8-a004-70a02b215c45/images/bffe77e0330e985ebfe7770fdedaf8abb962ea0ade8904f9c750655d53d21acb.png";

const IFRAME_URL = process.env.REACT_APP_DEMO_WIDGET_URL;

export default function Home() {
  return (
    <div className="bg-[#0A1628] text-[#F8FAFC]">
      {/* HERO */}
      <section className="relative overflow-hidden so-grain">
        <div
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage: `url(${heroBg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628]/40 via-[#0A1628]/70 to-[#0A1628]" />
        <div className="absolute -top-20 -left-20 w-[480px] h-[480px] so-glow-blue" />
        <div className="absolute bottom-0 right-0 w-[420px] h-[420px] so-glow-cyan" />

        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-24 lg:pt-24 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 min-w-0 so-rise">
            <h1 className="font-display font-extrabold text-[1.75rem] leading-[1.1] sm:text-5xl lg:text-6xl sm:leading-[1.05] tracking-tight">
              Let your website quote the job while you&apos;re on one.
            </h1>
            <p className="mt-6 text-[#94A3B8] text-base sm:text-lg max-w-xl leading-relaxed">
              GenQuotes embeds a smart quoting widget on your website. Customers
              get an instant price in seconds. You get their details the moment
              they accept — no phone tag, no back and forth.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/get-started"
                data-testid="hero-get-started-btn"
                className="btn-pink inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
              >
                Build my widget <ArrowRight size={16} />
              </Link>
              <Link
                to="/pricing"
                data-testid="hero-see-pricing-btn"
                className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm"
              >
                See pricing
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-4 sm:gap-8 max-w-xl">
              {[
                ["2 days", "configured for you"],
                ["$0", "setup fee"],
                ["28 days", "free trial — no lock-in"],
              ].map(([n, l]) => (
                <div key={l}>
                  <div className="font-display text-xl sm:text-2xl font-bold text-[#F8FAFC] whitespace-nowrap">{n}</div>
                  <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[#94A3B8] mt-2 leading-snug">{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Live embed inside a mock client website */}
          <div className="lg:col-span-6 min-w-0 relative so-rise" style={{ animationDelay: "120ms" }}>
            <div className="absolute -top-10 -right-10 w-[300px] h-[300px] so-glow-pink opacity-60" />

            {/* Browser chrome */}
            <div className="relative rounded-2xl border border-white/10 bg-[#0A1628]/70 backdrop-blur-xl overflow-hidden shadow-[0_30px_80px_-30px_rgba(6,182,212,0.45)]">
              {/* Window bar */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-[#0A1628]/90">
                <div className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded-full bg-[#EC4899]/80" />
                  <span className="w-3 h-3 rounded-full bg-[#06B6D4]/80" />
                  <span className="w-3 h-3 rounded-full bg-[#1B4FD8]/80" />
                </div>
                <div className="hidden sm:flex items-center gap-1 text-[#94A3B8] ml-1">
                  <ArrowLeft size={12} />
                  <ArrowRightIcon size={12} />
                  <RotateCw size={12} className="ml-1" />
                </div>
                <div className="flex-1 mx-2">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/[0.06] border border-white/10 text-[11px] font-mono text-[#94A3B8]">
                    <Lock size={11} className="text-[#06B6D4]" />
                    <span className="truncate">sparkleclean.example.com/get-a-quote</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-1 text-[#94A3B8]">
                  <Search size={12} />
                  <Menu size={12} />
                </div>
              </div>

              {/* Fake client site header */}
              <div className="bg-white">
                <div className="flex items-center justify-between gap-4 px-5 py-3 border-b border-slate-200">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="inline-flex shrink-0 items-center justify-center w-7 h-7 rounded-md bg-[#0A1628]">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#06B6D4]" />
                    </span>
                    <span className="font-display font-bold text-[14px] tracking-tight text-slate-900 truncate">
                      SparkleClean Co.
                    </span>
                  </div>
                  <span className="shrink-0 whitespace-nowrap inline-flex text-[11px] font-medium text-white bg-slate-900 rounded-full px-3 py-1.5">
                    Book now
                  </span>
                </div>

                {/* Secondary nav strip — evenly spaced, only shown when there's room */}
                <div className="hidden lg:flex items-center justify-center gap-8 px-5 py-2.5 border-b border-slate-200 bg-slate-50/60 text-[11px] uppercase tracking-widest text-slate-500">
                  <span>Services</span>
                  <span>About</span>
                  <span className="text-slate-900 font-medium">Get a Quote</span>
                  <span>Contact</span>
                </div>

                {/* Page section heading above the widget */}
                <div className="px-5 pt-5 pb-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 font-mono">
                    Step 1 of 1 · Instant quote
                  </div>
                  <div className="mt-1 font-display text-[18px] font-bold tracking-tight text-slate-900">
                    Get your cleaning quote in 60 seconds
                  </div>
                </div>

                {/* THE EMBEDDED WIDGET */}
                <iframe
                  data-testid="hero-quote-widget"
                  title="GenQuotes Live Quote Demo"
                  src={IFRAME_URL}
                  className="w-full"
                  style={{ height: 520, border: 0, display: "block", background: "#fff" }}
                />

                {/* Fake client site footer strip */}
                <div className="px-5 py-2.5 border-t border-slate-200 flex items-center justify-between text-[10px] text-slate-400">
                  <span>© SparkleClean Co.</span>
                  <span className="flex items-center gap-1">
                    Powered by <span className="text-[#06B6D4] font-medium">GenQuotes</span>
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-3 text-xs text-[#94A3B8] flex items-center gap-2 font-mono">
              <Activity size={12} className="text-[#06B6D4]" />
              This is exactly what your customers see — embedded inside your own site.
            </div>
          </div>
        </div>
      </section>

      {/* TRADES TAGLINE */}
      <section className="border-y border-white/5 bg-[#0A1628]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-center">
          <div className="text-xs sm:text-sm uppercase tracking-widest text-[#94A3B8] text-center">
            Built for service businesses that quote jobs — cleaning, trades, landscaping, and more
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="relative py-24 so-grain">
        <div className="absolute inset-0 so-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="max-w-3xl">
            <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">
              How it works
            </div>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Three steps from sign-up to your first quote.
            </h2>
            <p className="mt-4 text-[#94A3B8] max-w-xl leading-relaxed">
              We handle the math, the form, and the design. You drop a snippet
              on your site and you&apos;re live.
            </p>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {[
              {
                n: "01",
                t: "Tell us about your business",
                d: "Share your services and pricing. We configure your widget and have it ready within 2 business days.",
                i: PlugZap,
              },
              {
                n: "02",
                t: "Paste one line of code",
                d: "Drop a single snippet into your website. Works on WordPress, Squarespace, Wix, Webflow, and any custom site.",
                i: Layers,
              },
              {
                n: "03",
                t: "Quotes start flowing",
                d: "Customers get an instant price. The moment they accept, you receive their contact details straight to your inbox.",
                i: LineChart,
              },
            ].map((s, i) => (
              <div
                key={s.n}
                className="so-card rounded-xl p-6 relative group"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs tracking-widest text-[#06B6D4]">
                    STEP {s.n}
                  </span>
                  <s.i size={18} className="text-[#06B6D4]" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold tracking-tight">
                  {s.t}
                </h3>
                <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">{s.d}</p>
                <div className="mt-6 h-px w-full bg-gradient-to-r from-[#06B6D4]/40 via-[#1B4FD8]/30 to-transparent" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MID-PAGE CONSULTATION CALLOUT */}
      <section
        data-testid="consultation-callout"
        className="relative bg-[#0A1628] border-y border-[#06B6D4]/30 overflow-hidden"
      >
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] so-glow-cyan opacity-40 pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[360px] h-[360px] so-glow-blue opacity-40 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 py-16 sm:py-20 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#F8FAFC]">
            Ready to do more with AI?
          </h2>
          <p className="mt-4 text-[#94A3B8] text-base sm:text-lg leading-relaxed max-w-2xl mx-auto">
            Tell us what is slowing your business down. We will come back to
            you with honest thoughts on whether AI could help — no commitment,
            no sales pitch.
          </p>
          <div className="mt-8 flex justify-center">
            <button
              type="button"
              data-testid="callout-get-advice-btn"
              onClick={() => {
                document
                  .getElementById("free-consultation")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn-pink inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
            >
              Tell us about it <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">
                Capabilities
              </div>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
                A quoting widget that works the way your business does.
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-6 gap-5">
            <div className="md:col-span-4 so-card rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute -bottom-20 -right-20 w-[320px] h-[320px] so-glow-blue" />
              <Workflow size={22} className="text-[#06B6D4]" />
              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">
                Configured for your pricing
              </h3>
              <p className="mt-3 text-[#94A3B8] max-w-md text-sm leading-relaxed">
                Tell us your services, job types, and prices. We configure
                everything and have your widget ready within 2 business days.
              </p>
            </div>
            <div className="md:col-span-2 so-card rounded-2xl p-8">
              <Layers size={22} className="text-[#06B6D4]" />
              <h3 className="mt-5 font-display text-xl font-bold tracking-tight">
                Works on any website
              </h3>
              <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">
                Paste one line of code and your widget is live. Works on
                WordPress, Squarespace, Wix, Webflow, and any custom-built
                site. No developers needed.
              </p>
            </div>
            <div className="md:col-span-6 so-card rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-[320px] h-[320px] so-glow-cyan" />
              <Split size={22} className="text-[#06B6D4]" />
              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">
                Smart conditional questions
              </h3>
              <p className="mt-3 text-[#94A3B8] max-w-2xl text-sm leading-relaxed">
                Show customers only the questions relevant to their job. The
                right follow-up questions appear automatically based on what
                they select — no irrelevant options, no confusion.
              </p>
            </div>
            <div className="md:col-span-2 so-card rounded-2xl p-8">
              <Mail size={22} className="text-[#06B6D4]" />
              <h3 className="mt-5 font-display text-xl font-bold tracking-tight">
                Instant email notifications
              </h3>
              <p className="mt-3 text-[#94A3B8] text-sm leading-relaxed">
                The moment a customer accepts a quote, their name, phone
                number, and email land directly in your inbox. No delays, no
                missed leads.
              </p>
            </div>
            <div className="md:col-span-4 so-card rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute -top-16 -left-16 w-[280px] h-[280px] so-glow-cyan" />
              <Wand2 size={22} className="text-[#06B6D4]" />
              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">
                Your brand, your colours
              </h3>
              <p className="mt-3 text-[#94A3B8] max-w-md text-sm leading-relaxed">
                The widget displays your logo, your colours, and your business
                name. Your customers never see the GenQuotes name — just a
                seamless quoting experience that feels like part of your website.
              </p>
            </div>
            <div className="md:col-span-6 so-card rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute -bottom-24 -right-24 w-[360px] h-[360px] so-glow-blue" />
              <ShieldCheck size={22} className="text-[#06B6D4]" />
              <h3 className="mt-5 font-display text-2xl font-bold tracking-tight">
                Secure and reliable
              </h3>
              <p className="mt-3 text-[#94A3B8] max-w-2xl text-sm leading-relaxed">
                All customer data is encrypted in transit and at rest. Your
                customers&apos; information is stored securely and used only to
                connect them with your business.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FaqSection />

      {/* ASK US */}
      <AskUsSection />

      {/* FINAL CTA */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="relative overflow-hidden rounded-3xl border border-[#06B6D4]/20 bg-gradient-to-br from-[#0A1628] via-[#0A1628] to-[#0A1628] p-10 sm:p-14">
            <div className="absolute -top-24 -right-24 w-[420px] h-[420px] so-glow-pink" />
            <div className="absolute -bottom-32 -left-24 w-[420px] h-[420px] so-glow-blue" />
            <div className="relative">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
                Stop quoting on the phone. Let your site do it.
              </h2>
              <p className="mt-4 text-[#94A3B8] max-w-xl leading-relaxed">
                We configure your widget with your exact services and pricing — usually within 2 business days. No setup fee. Cancel anytime.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/get-started"
                  data-testid="cta-start-trial-btn"
                  className="btn-pink inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
                >
                  Build my widget <ArrowRight size={16} />
                </Link>
                <Link
                  to="/pricing"
                  data-testid="cta-pricing-btn"
                  className="btn-blue inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-medium"
                >
                  View pricing
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
